import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { notificationService } from "services/notificationService";
import type { AppNotification } from "types/domain";

interface InAppNotificationContextValue {
  notifications: AppNotification[];
  unreadCount: number;
  loading: boolean;
  fetchNotifications: () => Promise<void>;
  markAsRead: (id: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  refresh: () => void;
}

const InAppNotificationContext = createContext<InAppNotificationContextValue | undefined>(undefined);

const POLL_INTERVAL_MS = 60_000;

export const InAppNotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [tick, setTick] = useState(0);

  const fetchNotifications = useCallback(async () => {
    const token = sessionStorage.getItem("access_token");
    if (!token) return;

    setLoading(true);
    try {
      const [notifRes, countRes] = await Promise.all([
        notificationService.getMyNotifications(0, 20),
        notificationService.getUnreadCount(),
      ]);
      setNotifications(notifRes.items);
      setUnreadCount(countRes.unread_count);
    } catch {
      // silently fail — user may not be authenticated
    } finally {
      setLoading(false);
    }
  }, []);

  const markAsRead = useCallback(async (id: string) => {
    try {
      await notificationService.markAsRead(id);
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, is_read: true } : n))
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch {
      // ignore
    }
  }, []);

  const markAllAsRead = useCallback(async () => {
    try {
      await notificationService.markAllAsRead();
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch {
      // ignore
    }
  }, []);

  const refresh = useCallback(() => {
    setTick((t) => t + 1);
  }, []);

  // Fetch on mount + when refresh is called
  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications, tick]);

  // Poll every 60s
  useEffect(() => {
    const interval = setInterval(fetchNotifications, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  return (
    <InAppNotificationContext.Provider
      value={{
        notifications,
        unreadCount,
        loading,
        fetchNotifications,
        markAsRead,
        markAllAsRead,
        refresh,
      }}
    >
      {children}
    </InAppNotificationContext.Provider>
  );
};

export const useInAppNotifications = () => {
  const context = useContext(InAppNotificationContext);
  if (!context) {
    throw new Error("useInAppNotifications must be used within InAppNotificationProvider");
  }
  return context;
};
